import { RECEIVE_LOCATION_TYPE } from '../config/constants';
import type {
  ReceiveBalanceLineDto,
  ReceiveChildrenById,
  ReceiveConsignmentDto,
  ReceiveLocationDto,
  ReceiveLocationShipmentsDto,
  ReceiveShipmentBalancesDto,
  ReceiveTableRow
} from '../model/types';

/**
 * Label for a location row. Convoys read `sourceLocation` as the convoy name, so
 * they render `"<convoy> — <destination>"`; every other type is identified by the
 * destination it is arriving to (the origin is surfaced separately, muted).
 */
export const buildLocationLabel = (dto: ReceiveLocationDto): string => {
  if (dto.locationType === RECEIVE_LOCATION_TYPE.CONVOY) {
    return [dto.sourceLocation, dto.destinationLocation].filter(Boolean).join(' — ');
  }
  return dto.destinationLocation || dto.sourceLocation || '';
};

/**
 * Level-0 rows from `GET /locations`. No children are attached: `hasLazyChildren`
 * (driven by the BE's `consignmentCount`) is what makes the row expandable, and
 * the shipments call only fires when the user opens it.
 */
export const normalizeLocationRows = (dtolist: ReceiveLocationDto[] = []): ReceiveTableRow[] =>
  dtolist.map((dto, index) => ({
    id: dto.locationId ?? `location-${index}`,
    level: 0,
    rowType: 'location',
    locationId: dto.locationId,
    locationLabel: buildLocationLabel(dto),
    sourceLocation: dto.locationType === RECEIVE_LOCATION_TYPE.CONVOY ? undefined : dto.sourceLocation,
    locationType: dto.locationType,
    consignmentCount: dto.consignmentCount ?? 0,
    hasLazyChildren: (dto.consignmentCount ?? 0) > 0
  }));

/**
 * Consignment row. `dispatchDate` falls back to the parent date bucket, matching
 * the v1 behaviour: the leaf may omit it while the bucket still dates the batch.
 * Every consignment is expandable — the balances call is the only way to learn
 * whether it has lines, so the chevron is offered unconditionally.
 */
const toConsignmentRow = (
  dto: ReceiveConsignmentDto,
  level: number,
  parentId: string,
  index: number,
  bucketDate?: number | string
): ReceiveTableRow => ({
  id: `${parentId}-cons-${dto.consignmentId ?? index}`,
  level,
  parentId,
  rowType: 'consignment',
  consignmentId: dto.consignmentId,
  consignmentNumber: dto.consignmentNumber,
  waybill: dto.waybill,
  shipmentId: dto.shipmentId,
  truckNumber: dto.truckNumber,
  dispatchDate: dto.dispatchDate ?? bucketDate ?? null,
  organizationAbbreviation: dto.organizationAbbreviation,
  organizationDescription: dto.organizationDescription,
  hasLazyChildren: dto.shipmentId != null
});

/**
 * Turns one `/locations/{id}/shipments` response into `childrenById` fragments
 * for the expanded location.
 *
 * Grouped mode yields TWO levels from a single response (date rows plus their
 * consignments), so re-expanding a date costs no request — only the deeper
 * balances level is fetched separately.
 */
export const normalizeShipmentChildren = (
  locationRowId: string,
  dto: ReceiveLocationShipmentsDto | undefined,
  locationLevel = 0
): ReceiveChildrenById => {
  if (!dto) return {};

  const children: ReceiveChildrenById = {};

  if (dto.dates?.length) {
    children[locationRowId] = dto.dates.map((bucket, dateIndex) => {
      const dateRowId = `${locationRowId}-date-${dateIndex}`;
      const consignments = (bucket.consignments ?? []).map((consignment, index) =>
        toConsignmentRow(consignment, locationLevel + 2, dateRowId, index, bucket.date)
      );
      children[dateRowId] = consignments;

      return {
        id: dateRowId,
        level: locationLevel + 1,
        parentId: locationRowId,
        rowType: 'date',
        date: bucket.date,
        consignmentCount: bucket.consignmentCount ?? consignments.length,
        hasLazyChildren: consignments.length > 0
      };
    });

    return children;
  }

  children[locationRowId] = (dto.consignments ?? []).map((consignment, index) =>
    toConsignmentRow(consignment, locationLevel + 1, locationRowId, index)
  );

  return children;
};

/** Balance-line rows — the deepest level, from `GET /shipments/{id}/balances`. */
export const normalizeBalanceRows = (
  consignmentRowId: string,
  dto: ReceiveShipmentBalancesDto | undefined,
  consignmentLevel: number
): ReceiveTableRow[] =>
  (dto?.lines ?? []).map((line: ReceiveBalanceLineDto, index) => ({
    id: `${consignmentRowId}-line-${line.lineNumber ?? index}`,
    level: consignmentLevel + 1,
    parentId: consignmentRowId,
    rowType: 'balance',
    lineNumber: line.lineNumber,
    productDescription: line.productDescription,
    ownerItemDescription: line.ownerItemDescription,
    unitType: line.unitType,
    incomingBalance: line.incomingBalance,
    damagedBalance: line.damagedBalance,
    lostBalance: line.lostBalance
  }));

/**
 * Synthetic placeholder child (loading / empty / error) shown under an expanded
 * row. Keeping these as ordinary rows means the pending state rides the existing
 * hierarchical-table machinery instead of new shared render props.
 */
export const buildPlaceholderRow = (
  parentId: string,
  parentLevel: number,
  rowType: 'loading' | 'empty' | 'error'
): ReceiveTableRow => ({
  id: `${parentId}-${rowType}`,
  level: parentLevel + 1,
  parentId,
  rowType
});

/**
 * Depth-first flatten of the lazily-assembled tree into the flat array the
 * DataTable renders. Only loaded branches contribute rows; unexpanded or
 * not-yet-fetched rows simply have no entry in `childrenById`.
 */
export const buildReceiveRows = (roots: ReceiveTableRow[], childrenById: ReceiveChildrenById): ReceiveTableRow[] => {
  const rows: ReceiveTableRow[] = [];
  const seen = new Set<string>();

  const walk = (row: ReceiveTableRow) => {
    // Defensive: a cyclic parent chain must not recurse forever
    if (seen.has(row.id)) return;
    seen.add(row.id);

    rows.push(row);
    (childrenById[row.id] ?? []).forEach(walk);
  };

  roots.forEach(walk);

  return rows;
};
